/**
 * Default cover art ("Sade set: 5 kama ailesi", Default Thumbnails design
 * 8s): 5 compositions of cards fanned from a pin, in tints of one color,
 * drawn as an SVG so any
 * background or <img> can show it. `thumb` is the light card version; `hero`
 * the same geometry in deep tones for the landing page header. Units follow
 * the design: 1 = 1% of the height, `W` the width in those units.
 *
 * Mirrored in the learner app (utils/defaultCoverArt.ts); keep both in step.
 */

export const DEFAULT_COVER_VARIANTS = 5

/**
 * The art a course or path keeps for good: picked from its key ("course:12",
 * "collection:{guid}", as the catalog keys them), so it is the same in every
 * list and after every reload. Same hash in the admin and the learner app.
 */
export const coverVariantFor = key => {
  let hash = 0
  const text = String(key)
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) >>> 0
  return hash % DEFAULT_COVER_VARIANTS
}

const hexToRgb = hex => {
  let value = String(hex || '').replace('#', '')
  if (value.length === 3) value = value.replace(/./g, ch => ch + ch)
  const n = parseInt(value.slice(0, 6), 16)
  return Number.isNaN(n) ? [1, 95, 251] : [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const toHex = rgb =>
  `#${rgb
    .map(c => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, '0'))
    .join('')}`

const toLinear = c => {
  const v = c / 255
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
}

const fromLinear = v => 255 * (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055)

const toOklab = rgb => {
  const [r, g, b] = rgb.map(toLinear)
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

const fromOklab = ([L, a, b]) => {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ].map(fromLinear)
}

/**
 * `percent` of the color, the rest white, mixed as the design's
 * `color-mix(in oklch, color p%, white)`: calmer tints than mixing in RGB.
 */
const tint = (hex, percent) => {
  const p = percent / 100
  const [L, a, b] = toOklab(hexToRgb(hex))
  return toHex(fromOklab([L * p + (1 - p), a * p, b * p]))
}

/**
 * Rounded rectangle path; radii [tl, tr, br, bl]. Radii that don't fit are
 * scaled down together, as CSS border-radius does (so r r 0 0 on a 2r × r box
 * is a half circle).
 */
const rectPath = (x, y, w, h, [tl, tr, br, bl]) => {
  const f = Math.min(
    1,
    w / (tl + tr || 1),
    w / (bl + br || 1),
    h / (tl + bl || 1),
    h / (tr + br || 1),
  )
  const [a, b, c, d] = [tl * f, tr * f, br * f, bl * f]
  return (
    `M${x + a},${y}H${x + w - b}` +
    (b ? `A${b},${b} 0 0 1 ${x + w},${y + b}` : '') +
    `V${y + h - c}` +
    (c ? `A${c},${c} 0 0 1 ${x + w - c},${y + h}` : '') +
    `H${x + d}` +
    (d ? `A${d},${d} 0 0 1 ${x},${y + h - d}` : '') +
    `V${y + a}` +
    (a ? `A${a},${a} 0 0 1 ${x + a},${y}` : '') +
    'Z'
  )
}

const shapesFor = (variant, col, hero, W) => {
  // The design's tints: light in the card; in the hero, close to the color.
  const T = p => tint(col, hero ? (p <= 6 ? 83 : Math.min(95, 85 + p * 0.18)) : p)
  const S = hero ? 1.8 : 1
  const T4 = [T(12), T(28), T(55), col]
  const T3 = [T(16), T(45), col]
  const L = 210 * (hero ? 1.6 : 1)
  // [x, y, w, h, radii, fill, rotate, originX, originY, shadow]
  const R = (x, y, w, h, r, fill, rot = 0, ox = x + w / 2, oy = y + h / 2, shadow = true) => ({
    x, y, w, h, r: Array.isArray(r) ? r : [r, r, r, r], fill, rot, ox, oy, shadow,
  })
  const fanRight = (angles, top, tones) =>
    angles.map((a, i) => R(W + 2 - L, top, L, 140, [4, 0, 0, 0], tones[i], a, W + 2, top))

  switch (variant % DEFAULT_COVER_VARIANTS) {
    // 1. Fan from the right
    case 0:
      return fanRight([14, 4, -6, -17], 45, T4)
    // 2. Rays from the corner
    case 1:
      return (hero ? [-40, -26, -13, -3] : [-66, -45, -24, -6]).map((a, i) =>
        R(-8, 82, 230 * S, 48, [0, 6, 6, 0], T4[i], a, -8, 106))
    // 3. Three-card fan
    case 2:
      return fanRight([4, -9, -24], 56, T3)
    // 4. Steep fan
    case 3:
      return fanRight([34, 22, 10, -2], 84, T4)
    // 5. Diagonal stack
    default:
      return [0, 1, 2, 3].map(i =>
        R(-24 + i * 12, 26 + i * 17, 175 * S, 50, 6, T4[i], -16))
  }
}

/**
 * The art as an SVG string. `aspect` is width / height of the box it fills
 * (cards about 1.6, headers about 2.9); it is drawn for that shape and
 * covers any other by cropping.
 */
export const coverArtSvg = (color, variant = 0, mode = 'thumb', aspect) => {
  const hero = mode === 'hero'
  const col = /^#[0-9a-f]{3,8}$/i.test(String(color || '').trim())
    ? toHex(hexToRgb(String(color).trim()))
    : '#015ffb'
  const W = 100 * (aspect || (hero ? 2.9 : 1.6))
  const bg = tint(col, hero ? 83 : 4)
  const shapes = shapesFor(variant, col, hero, W)
    .map(s => {
      const transform = s.rot ? ` transform='rotate(${s.rot} ${s.ox} ${s.oy})'` : ''
      const filter = s.shadow ? " filter='url(#s)'" : ''
      return `<path d='${rectPath(s.x, s.y, s.w, s.h, s.r)}' fill='${s.fill}'${transform}${filter}/>`
    })
    .join('')
  return (
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${W} 100' preserveAspectRatio='xMidYMid slice'>` +
    `<defs><filter id='s' x='-20%' y='-20%' width='140%' height='140%'>` +
    `<feDropShadow dx='0' dy='0' stdDeviation='1.2' flood-color='#00143c' flood-opacity='${hero ? 0.12 : 0.1}'/>` +
    `</filter></defs><rect width='${W}' height='100' fill='${bg}'/>${shapes}</svg>`
  )
}

/** The art as a data: URL (quotes and parentheses escaped for url() and style=""). */
export const coverArtUrl = (color, variant, mode, aspect) =>
  `data:image/svg+xml,${encodeURIComponent(coverArtSvg(color, variant, mode, aspect))
    .replace(/'/g, '%27')
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29')}`

/** The art's background color, shown before it paints. */
export const coverArtBackground = (color, mode = 'thumb') =>
  tint(
    /^#[0-9a-f]{3,8}$/i.test(String(color || '').trim()) ? String(color).trim() : '#015ffb',
    mode === 'hero' ? 83 : 4,
  )
